import type { ThemeClasses } from "../../types/components";
import {
  ATTENDANCE_STATUSES,
  CLOCK_SOURCES,
  ADJUSTMENT_TYPES,
  ADJUSTMENT_STATUSES,
  WORK_LOG_TYPES,
  WORK_LOG_STATUSES,
  WORK_LOG_ORIGINS,
  CONTRIBUTION_TYPES,
  PAY_FREQUENCIES,
  PAYROLL_TYPES,
  PERIOD_STATUSES,
  PAYSLIP_STATUSES,
} from "@torre/payroll";

// themeClasses estándar compartido por la mini-app de Nómina (mismo shape que
// el usado por el Modal base y las demás secciones — soporta dark mode).
export const buildThemeClasses = (isDarkMode: boolean): ThemeClasses => ({
  bg: isDarkMode ? "bg-dark-surface" : "bg-white",
  border: isDarkMode ? "border-dark-border" : "border-gray-200",
  text: isDarkMode ? "text-white" : "text-gray-900",
  textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
  cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
  cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
  buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
  buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
  buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
});

// --- Vocabularios (en sync con @torre/payroll) -------------------------------
export const ATTENDANCE_STATUS_OPTIONS = ATTENDANCE_STATUSES;
export const CLOCK_SOURCE_OPTIONS = CLOCK_SOURCES;
export const ADJUSTMENT_TYPE_OPTIONS = ADJUSTMENT_TYPES;
export const ADJUSTMENT_STATUS_OPTIONS = ADJUSTMENT_STATUSES;
export const WORK_LOG_TYPE_OPTIONS = WORK_LOG_TYPES;
export const WORK_LOG_STATUS_OPTIONS = WORK_LOG_STATUSES;
export const WORK_LOG_ORIGIN_OPTIONS = WORK_LOG_ORIGINS;
export const CONTRIBUTION_TYPE_OPTIONS = CONTRIBUTION_TYPES;
export const FREQUENCY_OPTIONS = PAY_FREQUENCIES;
export const PAYROLL_TYPE_OPTIONS = PAYROLL_TYPES;
export const PERIOD_STATUS_OPTIONS = PERIOD_STATUSES;
export const PAYSLIP_STATUS_OPTIONS = PAYSLIP_STATUSES;

// ---------------------------------------------------------------------------
// ETIQUETAS — se resuelven por i18n, NO en español fijo.
//
// El módulo ya traducía los motivos de pago no calculable pero pintaba los
// estados, tipos y modalidades en español duro: un usuario en inglés veía la
// interfaz traducida y los badges/selectores en castellano.
// ---------------------------------------------------------------------------

/** Firma mínima de la `t` de react-i18next. */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Construye el resolvedor de etiquetas de un vocabulario.
 *
 * - valor ausente/vacío → "—".
 * - código sin traducción → el propio código (jamás una celda vacía).
 */
const vocabulary =
  (prefix: string) =>
  (t: TranslateFn, code: unknown): string => {
    const raw = code === null || code === undefined ? "" : String(code).trim();
    if (raw === "") return "—";
    return t(`${prefix}.${raw}`, { defaultValue: raw });
  };

/** Estado de la jornada (AgroCloud#36). */
export const attendanceStatusLabel = vocabulary("payroll.attendanceStatuses");
/** Fuente del marcaje (AgroCloud#36 / AgroCloud#83). */
export const clockSourceLabel = vocabulary("payroll.clockSources");
/** Tipos de haber/descuento (AgroCloud#37). */
export const adjustmentTypeLabel = vocabulary("payroll.adjustmentTypes");
export const adjustmentStatusLabel = vocabulary("payroll.adjustmentStatuses");
/** Tipos de incidencia del equipo (AgroCloud#81). */
export const workLogTypeLabel = vocabulary("payroll.workLogTypes");
/** Flujo de autorización (AgroCloud#81). */
export const workLogStatusLabel = vocabulary("payroll.workLogStatuses");
export const workLogOriginLabel = vocabulary("payroll.workLogOrigins");
/** Parámetros previsionales (AgroCloud#38). */
export const contributionTypeLabel = vocabulary("payroll.contributionTypes");
/** Periodos (AgroCloud#82). */
export const frequencyLabel = vocabulary("payroll.frequencies");
export const payrollTypeLabel = vocabulary("payroll.payrollTypes");
export const periodStatusLabel = vocabulary("payroll.periodStatuses");
/** Liquidaciones (AgroCloud#38). */
export const payslipStatusLabel = vocabulary("payroll.payslipStatuses");
/**
 * Por qué una liquidación (o una de sus líneas) puede quedar incompleta:
 * un valor de `LINE_STATUSES` de @torre/payroll. Incluye los dos estados de
 * las modalidades por hora y de sueldo fijo (#530) — sin ellos, un colaborador
 * por hora sin tarifa mostraba el identificador interno `no_hourly_rate`.
 */
export const paymentStatusLabel = vocabulary("payroll.paymentStatuses");
/**
 * Mensaje de negocio de cada `noCalcReason` que devuelve Colaboradores
 * (AgroCloud#100). Cubre TODOS los motivos de `NO_CALC_REASONS`, para que el
 * usuario sepa exactamente qué corregir en vez de leer un código.
 */
export const noCalcReasonLabel = vocabulary("payroll.noCalc");
/** Estado fiscal del comprobante (AgroCloud#82). */
export const stampStatusLabel = vocabulary("payroll.stampStatuses");

// --- Tonos (puramente visuales, no traducibles) ------------------------------

export const ATTENDANCE_STATUS_TONES: Record<string, string> = {
  present: "bg-emerald-500/15 text-emerald-500",
  half_day: "bg-sky-500/15 text-sky-500",
  absent: "bg-red-500/15 text-red-500",
  leave: "bg-amber-500/15 text-amber-500",
  sick_leave: "bg-violet-500/15 text-violet-500",
  vacation: "bg-cyan-500/15 text-cyan-500",
  holiday: "bg-gray-400/15 text-gray-500",
};

export const ADJUSTMENT_TYPE_TONES: Record<string, string> = {
  advance: "bg-amber-500/15 text-amber-500",
  loan: "bg-orange-500/15 text-orange-500",
  deduction: "bg-red-500/15 text-red-500",
  earning: "bg-emerald-500/15 text-emerald-500",
  bonus: "bg-sky-500/15 text-sky-500",
};

export const ADJUSTMENT_STATUS_TONES: Record<string, string> = {
  pending: "bg-amber-500/15 text-amber-500",
  applied: "bg-emerald-500/15 text-emerald-500",
  void: "bg-gray-400/15 text-gray-500",
};

export const WORK_LOG_STATUS_TONES: Record<string, string> = {
  submitted: "bg-amber-500/15 text-amber-500",
  approved: "bg-emerald-500/15 text-emerald-500",
  rejected: "bg-red-500/15 text-red-500",
  sent_to_payroll: "bg-sky-500/15 text-sky-500",
};

export const PERIOD_STATUS_TONES: Record<string, string> = {
  open: "bg-sky-500/15 text-sky-500",
  calculated: "bg-amber-500/15 text-amber-500",
  closed: "bg-emerald-500/15 text-emerald-500",
};

export const PAYSLIP_STATUS_TONES: Record<string, string> = {
  draft: "bg-gray-400/15 text-gray-500",
  calculated: "bg-sky-500/15 text-sky-500",
  approved: "bg-emerald-500/15 text-emerald-500",
  paid: "bg-emerald-600/15 text-emerald-600",
  void: "bg-red-500/15 text-red-500",
};

/**
 * Un tono por cada `LINE_STATUSES` de @torre/payroll. `team_module_missing` es
 * el estado real mientras el módulo Equipo (AgroCloud#13 / PR AgroCloud#100) no
 * esté mergeado: la asistencia se guarda, el sueldo queda sin calcular y se
 * dice por qué — nunca un 0.
 */
export const PAYMENT_STATUS_TONES: Record<string, string> = {
  ok: "bg-emerald-500/15 text-emerald-500",
  no_rate: "bg-gray-400/15 text-gray-500",
  no_daily_rate: "bg-amber-500/15 text-amber-500",
  no_hourly_rate: "bg-amber-500/15 text-amber-500",
  no_salary: "bg-amber-500/15 text-amber-500",
  no_payment_calc: "bg-amber-500/15 text-amber-500",
  team_module_missing: "bg-amber-500/15 text-amber-500",
  // Motivo DERIVADO: el problema real está en el haber que quedó sin monto.
  no_taxable_base: "bg-amber-500/15 text-amber-500",
};

export const STAMP_STATUS_TONES: Record<string, string> = {
  not_stamped: "bg-gray-400/15 text-gray-500",
  pending: "bg-sky-500/15 text-sky-500",
  stamped: "bg-emerald-500/15 text-emerald-500",
  error: "bg-red-500/15 text-red-500",
  no_pac_configured: "bg-amber-500/15 text-amber-500",
};

/** Formatea un monto como moneda local (sin depender del locale del navegador). */
export function formatMoney(
  amount: number | undefined | null,
  currency = "MXN",
): string {
  const n = Number(amount) || 0;
  return `${currency} ${n.toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;
}

/**
 * Formatea un importe que puede ser DESCONOCIDO (AgroCloud#38).
 *
 * `null`/`undefined` se muestran como "—", NO como 0: un importe que no se pudo
 * calcular no es un sueldo de cero, y confundirlos es pagarle mal a alguien.
 */
export function formatAmount(
  amount: number | null | undefined,
  currency = "MXN",
): string {
  if (amount === null || amount === undefined) return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return formatMoney(n, currency);
}

/** Formatea jornadas con hasta 2 decimales ("2,5 jornadas"). */
export function formatDays(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("es-MX", { maximumFractionDigits: 2 });
}
